import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Cloud } from "lucide-react";
import Avatar from "./Avatar";
import { createTask, createHabit, createReminder, createListItem } from "../lib/api";
import {
    getPermissionState,
    setPermissionState,
    getCachedWeather,
    requestWeatherPermissionAndFetch,
} from "../lib/weatherClient";
import { pickWeatherGreeting, weatherChipLabel } from "../lib/weatherPhrases";

const TYPES = [
    { id: "task", label: "Task", color: "terracotta" },
    { id: "habit", label: "Habit", color: "sage" },
    { id: "reminder", label: "Reminder", color: "mustard" },
    { id: "list", label: "List", color: "warm" },
];

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function MorningCheckIn({ open, onComplete, greetingMessage, avatarStyle = "feminine", displayName = "" }) {
    const [step, setStep] = useState(0); // 0: greeting, 1+: per type
    const [items, setItems] = useState({ task: [], habit: [], reminder: [], list: [] });

    // Weather sky-peek state — see lib/weatherClient.js for the permission ladder.
    // `weather` is the snapshot once available; `weatherLine` is the picked phrase
    // we splice into Mochi's greeting paragraph. `skyAsk` toggles the sky-peek
    // prompt — shown only if permission hasn't been decided yet AND we're on step 0.
    const [weather, setWeather] = useState(() => getCachedWeather());
    const [weatherLine, setWeatherLine] = useState(() => pickWeatherGreeting(getCachedWeather()));
    const [skyAsk, setSkyAsk] = useState(() => getPermissionState() == null);
    const [skyBusy, setSkyBusy] = useState(false);

    useEffect(() => {
        // If permission was granted in a past session but we don't have a fresh
        // cached snapshot, do a silent background fetch (no prompt — getCurrentPosition
        // re-resolves immediately for granted permissions).
        if (!open) return;
        if (getPermissionState() === "granted" && !weather) {
            (async () => {
                const snap = await requestWeatherPermissionAndFetch();
                if (snap) {
                    setWeather(snap);
                    setWeatherLine(pickWeatherGreeting(snap));
                    try { window.dispatchEvent(new Event("mochi-weather-changed")); } catch { /* ignore */ }
                }
            })();
        }
    }, [open, weather]);

    const handleAllowSky = async () => {
        setSkyBusy(true);
        const snap = await requestWeatherPermissionAndFetch();
        setSkyBusy(false);
        setSkyAsk(false);
        if (snap) {
            setWeather(snap);
            setWeatherLine(pickWeatherGreeting(snap));
            try { window.dispatchEvent(new Event("mochi-weather-changed")); } catch { /* ignore */ }
        }
    };

    const handleDeclineSky = () => {
        setPermissionState("denied");
        setSkyAsk(false);
    };

    if (!open) return null;

    const currentType = step >= 1 && step <= 4 ? TYPES[step - 1] : null;

    const finish = async () => {
        // Persist all items
        const promises = [];
        items.task.forEach((it) =>
            promises.push(createTask({ title: it.title, priority: it.priority || "medium", due_time: it.due_time || null }))
        );
        items.habit.forEach((it) =>
            promises.push(createHabit({ title: it.title, schedule_days: it.schedule_days, time: it.time || null }))
        );
        items.reminder.forEach((it) =>
            promises.push(createReminder({ title: it.title, time: it.time }))
        );
        items.list.forEach((it) => promises.push(createListItem({ title: it.title, quantity: it.quantity || null })));
        await Promise.all(promises);
        onComplete();
    };

    return (
        <AnimatePresence>
            <motion.div
                key="overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-cream/95 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
                data-testid="morning-checkin-overlay"
            >
                <motion.div
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 220, damping: 24 }}
                    className="w-full max-w-md bg-surface rounded-clay shadow-clay p-5 sm:p-8 my-4 relative"
                    style={{ paddingBottom: "max(env(safe-area-inset-bottom), 1.25rem)" }}
                >
                    <div className="flex items-center justify-between mb-2">
                        <span className="font-display text-xs uppercase tracking-widest text-warm-500" data-testid="morning-step-label">
                            {step === 0 ? "Morning ✨" : `Step ${step} of 4`}
                        </span>
                        <button
                            className="rounded-full p-1.5 hover:bg-surface-hover text-warm-500 transition-colors"
                            onClick={onComplete}
                            data-testid="morning-checkin-close"
                            aria-label="Close"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    {step === 0 && (
                        <div className="text-center pt-2">
                            <div className="flex justify-center -mt-2">
                                <Avatar mood="excited" size={180} style={avatarStyle} />
                            </div>
                            <h2 className="font-display text-3xl text-warm-900 mt-2" data-testid="morning-greeting-title">
                                {displayName ? `Good morning, ${displayName}!` : "Good morning!"}
                            </h2>
                            <p className="font-body text-warm-800 mt-3 px-2">{greetingMessage || "Let's plan a soft, lovely day together. 🌸"}</p>
                            {weather && weatherLine && (
                                <div
                                    className="mt-3 mx-auto inline-flex items-center gap-2 bg-cream rounded-full px-3 py-1.5 border border-warm-300/30"
                                    data-testid="morning-weather-chip"
                                >
                                    <Cloud size={14} className="text-warm-500" />
                                    <span className="font-body text-xs text-warm-800">{weatherChipLabel(weather)}</span>
                                </div>
                            )}
                            {weather && weatherLine && (
                                <p
                                    className="font-body text-warm-800 mt-2 px-2 italic"
                                    data-testid="morning-weather-line"
                                >
                                    {weatherLine}
                                </p>
                            )}
                            {skyAsk && (
                                <div
                                    className="mt-4 mx-2 bg-cream rounded-2xl border border-warm-300/40 p-3 text-left"
                                    data-testid="morning-sky-ask"
                                >
                                    <p className="font-body text-sm text-warm-900">
                                        can i peek at the sky today? 🌤️ <span className="text-warm-500">— so i know if it's a sunshine or cozy-rain day for us.</span>
                                    </p>
                                    <div className="mt-2 flex gap-2">
                                        <button
                                            onClick={handleAllowSky}
                                            disabled={skyBusy}
                                            className="flex-1 bg-sage hover:opacity-90 disabled:opacity-50 text-cream font-display text-sm py-2 rounded-full transition-all"
                                            data-testid="morning-sky-allow"
                                        >
                                            {skyBusy ? "peeking…" : "yes, peek 🌤️"}
                                        </button>
                                        <button
                                            onClick={handleDeclineSky}
                                            className="flex-1 bg-surface hover:bg-surface-hover text-warm-800 font-display text-sm py-2 rounded-full transition-all"
                                            data-testid="morning-sky-decline"
                                        >
                                            not today
                                        </button>
                                    </div>
                                </div>
                            )}
                            <button
                                onClick={() => setStep(1)}
                                className="mt-6 w-full bg-terracotta hover:bg-terracotta-hover text-cream font-display text-lg py-3.5 rounded-full shadow-clay hover:shadow-float hover:-translate-y-0.5 transition-all"
                                data-testid="morning-start-btn"
                            >
                                Let's set up the day
                            </button>
                            <button
                                onClick={onComplete}
                                className="mt-2 w-full bg-cream hover:bg-cream/70 text-warm-900 font-display text-sm py-3 rounded-full border border-warm-300/40 transition-all"
                                data-testid="morning-skip-btn"
                            >
                                Just show me my list 🌸
                            </button>
                            <p className="font-body text-[10px] text-warm-500 mt-2 italic">
                                you can plan whenever — mochi isn't going anywhere 🍡
                            </p>
                        </div>
                    )}

                    {currentType && (
                        <StepBuilder
                            type={currentType}
                            items={items[currentType.id]}
                            setItems={(v) => setItems((s) => ({ ...s, [currentType.id]: v }))}
                            onNext={() => (step < 4 ? setStep(step + 1) : finish())}
                            onBack={() => setStep(step - 1)}
                            isLast={step === 4}
                        />
                    )}
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}

function StepBuilder({ type, items, setItems, onNext, onBack, isLast }) {
    const [draft, setDraft] = useState({});

    const titles = {
        task: { h: "What needs to get done?", hint: "Add today's tasks. You can prioritise the ones that matter most." },
        habit: { h: "Any habits to nurture?", hint: "Tiny daily wins. Pick days you want to practice." },
        reminder: { h: "Anything to remember?", hint: "Quick nudges at a specific time." },
        list: { h: "Need to grab anything?", hint: "Shopping or a quick to-do list." },
    };

    const add = () => {
        if (!draft.title) return;
        const item = { ...draft, _key: (crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`) };
        if (type.id === "habit" && !item.schedule_days) item.schedule_days = [0, 1, 2, 3, 4, 5, 6];
        if (type.id === "task" && !item.priority) item.priority = "medium";
        setItems([...items, item]);
        setDraft({});
    };

    return (
        <div>
            <div className="flex items-center gap-3 mb-2">
                <Sparkles className="text-mustard" size={20} />
                <h3 className="font-display text-2xl text-warm-900">{titles[type.id].h}</h3>
            </div>
            <p className="font-body text-warm-500 text-sm mb-4">{titles[type.id].hint}</p>

            <div className="space-y-3 max-h-64 overflow-y-auto pr-1 mb-3">
                {items.map((it, idx) => (
                    <motion.div
                        key={it._key || idx}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-cream rounded-2xl px-4 py-3 flex items-center gap-2 border border-warm-300/30"
                        data-testid={`morning-${type.id}-item-${idx}`}
                    >
                        <span className="font-body text-warm-900 flex-1 truncate">{it.title}</span>
                        {type.id === "task" && <PriorityChip p={it.priority} />}
                        {type.id === "habit" && (
                            <span className="font-body text-xs text-warm-500">{it.schedule_days?.length === 7 ? "Daily" : `${it.schedule_days?.length} days`}</span>
                        )}
                        {type.id === "reminder" && it.time && <span className="font-body text-xs text-warm-500">{it.time}</span>}
                        <button
                            onClick={() => setItems(items.filter((_, i) => i !== idx))}
                            className="text-warm-500 hover:text-terracotta"
                            aria-label="Remove"
                        >
                            <X size={14} />
                        </button>
                    </motion.div>
                ))}
            </div>

            <div className="bg-cream rounded-2xl p-3 border border-warm-300/30">
                <input
                    type="text"
                    placeholder={`New ${type.label.toLowerCase()}…`}
                    value={draft.title || ""}
                    onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                    onKeyDown={(e) => e.key === "Enter" && add()}
                    className="w-full bg-transparent font-body text-warm-900 placeholder:text-warm-500 outline-none px-1 py-1"
                    data-testid={`morning-${type.id}-input`}
                />

                {type.id === "task" && (
                    <div className="flex gap-2 mt-2">
                        {["low", "medium", "high"].map((p) => (
                            <button
                                key={p}
                                onClick={() => setDraft({ ...draft, priority: p })}
                                className={`px-3 py-1 rounded-full text-xs font-display capitalize transition-all ${
                                    (draft.priority || "medium") === p
                                        ? "bg-terracotta text-cream"
                                        : "bg-surface text-warm-800 hover:bg-surface-hover"
                                }`}
                                data-testid={`morning-task-priority-${p}`}
                            >
                                {p}
                            </button>
                        ))}
                        <input
                            type="time"
                            value={draft.due_time || ""}
                            onChange={(e) => setDraft({ ...draft, due_time: e.target.value })}
                            className="ml-auto bg-surface rounded-full px-2 text-xs font-body text-warm-800 outline-none"
                            data-testid="morning-task-time"
                        />
                    </div>
                )}

                {type.id === "habit" && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                        {DAYS.map((d, i) => {
                            const sel = draft.schedule_days?.includes(i) ?? true;
                            return (
                                <button
                                    key={d}
                                    onClick={() => {
                                        const cur = draft.schedule_days ?? [0, 1, 2, 3, 4, 5, 6];
                                        const next = sel ? cur.filter((x) => x !== i) : [...cur, i];
                                        setDraft({ ...draft, schedule_days: next });
                                    }}
                                    className={`px-2.5 py-1 rounded-full text-xs font-display transition-all ${
                                        sel ? "bg-sage text-cream" : "bg-surface text-warm-500"
                                    }`}
                                    data-testid={`morning-habit-day-${i}`}
                                >
                                    {d}
                                </button>
                            );
                        })}
                    </div>
                )}

                {type.id === "reminder" && (
                    <div className="mt-2">
                        <input
                            type="time"
                            value={draft.time || ""}
                            onChange={(e) => setDraft({ ...draft, time: e.target.value })}
                            className="bg-surface rounded-full px-3 py-1 text-xs font-body text-warm-800 outline-none"
                            data-testid="morning-reminder-time"
                        />
                    </div>
                )}

                <button
                    onClick={add}
                    disabled={!draft.title || (type.id === "reminder" && !draft.time)}
                    className="mt-2 w-full bg-warm-900 hover:bg-warm-800 disabled:opacity-40 text-cream font-display text-sm py-2 rounded-full transition-all"
                    data-testid={`morning-${type.id}-add`}
                >
                    + Add
                </button>
            </div>

            <div className="flex gap-2 mt-5">
                <button
                    onClick={onBack}
                    className="flex-1 bg-surface hover:bg-surface-hover font-display text-warm-800 py-3 rounded-full transition-all"
                    data-testid="morning-back-btn"
                >
                    Back
                </button>
                <button
                    onClick={onNext}
                    className="flex-[2] bg-terracotta hover:bg-terracotta-hover text-cream font-display text-base py-3 rounded-full shadow-clay hover:shadow-float hover:-translate-y-0.5 transition-all"
                    data-testid={isLast ? "morning-finish-btn" : "morning-next-btn"}
                >
                    {isLast ? "Done — let's go!" : "Next →"}
                </button>
            </div>
        </div>
    );
}

function PriorityChip({ p }) {
    const map = {
        low: "bg-sage-soft text-warm-900",
        medium: "bg-mustard-soft text-warm-900",
        high: "bg-terracotta text-cream",
    };
    return <span className={`text-[10px] uppercase font-display tracking-wider px-2 py-0.5 rounded-full ${map[p] || map.medium}`}>{p}</span>;
}
